import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../theme/ThemeProvider';
import { useAuth } from '../auth/AuthContext';
import { Users, BarChart3, Shield, Activity, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function AdminPage() {
  const { t, glassCard } = useTheme();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_API_URL || '/api';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/users`, { headers }).then(r => r.json()),
      ]);
      if (statsRes.success) setStats(statsRes.stats);
      if (usersRes.success) setUsers(usersRes.users);
    } catch (e) { console.error('Admin fetch failed:', e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const iconBox = { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const badgeStyle = { borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', display: 'inline-block' };

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 80px', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ ...iconBox, background: 'linear-gradient(135deg, rgba(143,183,255,0.22), rgba(125,249,198,0.14))', border: `1px solid rgba(143,183,255,0.28)` }}>
              <Shield size={20} color="#8fb7ff" />
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800 }}>Admin Panel</h1>
              <p style={{ fontSize: 12, color: t.textDim }}>System overview and user management</p>
            </div>
          </div>
          <button onClick={fetchData} disabled={loading} style={{
            background: t.btnIconBg, border: `1px solid ${t.btnIconBorder}`, borderRadius: 10,
            color: t.textMuted, cursor: 'pointer', padding: '8px 16px', fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s',
          }}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid-mobile-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Total Users', value: stats.total_users, icon: Users, color: '#8fb7ff', bg: 'rgba(143,183,255,0.12)' },
              { label: 'Pro Plans', value: stats.pro_subscribers, icon: BarChart3, color: '#7df9c6', bg: 'rgba(125,249,198,0.12)' },
              { label: 'Max Plans', value: stats.max_subscribers, icon: BarChart3, color: '#f2c978', bg: 'rgba(242,201,120,0.12)' },
              { label: 'Signals Processed', value: stats.total_signals, icon: Activity, color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
            ].map((s, i) => (
              <div key={i} className="card-enter" style={{ ...glassCard, padding: '20px 24px', animationDelay: `${i * 0.06}s` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ ...iconBox, background: s.bg }}><s.icon size={18} color={s.color} /></div>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint }}>{s.label}</span>
                </div>
                <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.02em' }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* System Status */}
        {stats && (
          <div className="card-enter" style={{ ...glassCard, padding: 24, marginBottom: 32, animationDelay: '0.24s' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Activity size={15} color={t.textMuted} /> System Status
            </h2>
            <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {[
                { label: 'Trading Engine', active: stats.system_running },
                { label: 'MT5 Connection', active: stats.mt5_connected },
                { label: 'Telegram Connection', active: stats.telegram_connected },
              ].map((s, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                  background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 12,
                }}>
                  {s.active ? <Wifi size={16} color={t.positive} /> : <WifiOff size={16} color={t.negative} />}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: s.active ? t.positive : t.textDim, fontWeight: 600 }}>
                      {s.active ? 'Connected' : 'Disconnected'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="card-enter" style={{ ...glassCard, padding: 24, animationDelay: '0.3s' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={15} color={t.textMuted} /> Registered Users
            </h2>
            <span style={{ fontSize: 12, color: t.textFaint, background: t.countBg, border: `1px solid ${t.countBorder}`, borderRadius: 6, padding: '2px 8px' }}>
              {users.length}
            </span>
          </div>

          {users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: t.textFaint }}>
              <Users size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
              <div style={{ fontSize: 13 }}>No users registered yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Header */}
              <div className="admin-users-header hide-mobile" style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 100px 100px 140px', gap: 12, padding: '0 16px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint }}>
                <span>ID</span><span>Name</span><span>Email</span><span>Plan</span><span>Role</span><span>Joined</span>
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                {users.map((u) => (
                  <div key={u.id} className="admin-users-grid" style={{
                    background: t.rowBg, backdropFilter: 'blur(24px)', border: `1px solid ${t.rowBorder}`, borderRadius: 12,
                    padding: '12px 16px', display: 'grid', gridTemplateColumns: '40px 1fr 1fr 100px 100px 140px', gap: 12, alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 12, fontFamily: 'monospace', color: t.textDim }}>#{u.id}</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{u.display_name || '-'}</span>
                    <span style={{ fontSize: 12, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{
                        ...badgeStyle,
                        background: u.tier === 'max' ? 'rgba(242,201,120,0.14)' : t.badgeBuyBg,
                        color: u.tier === 'max' ? '#f2c978' : t.badgeBuyText,
                        border: `1px solid ${u.tier === 'max' ? 'rgba(242,201,120,0.28)' : t.badgeBuyBorder}`,
                        textTransform: 'uppercase',
                      }}>{u.tier || 'none'}</span>
                      <span style={{
                        ...badgeStyle,
                        background: u.is_admin ? 'rgba(251,191,36,0.12)' : t.sectionBg,
                        color: u.is_admin ? '#fbbf24' : t.textDim,
                        border: `1px solid ${u.is_admin ? 'rgba(251,191,36,0.25)' : t.sectionBorder}`,
                      }}>{u.is_admin ? 'ADMIN' : 'USER'}</span>
                    </div>
                    <span className="hide-mobile" style={{ fontSize: 11, fontFamily: 'monospace', color: t.textDim }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
